import { useTranslation } from 'react-i18next';
import { Heading, Table, Text } from '@bn-apps/dim-ui-react';
import { ArrowOutward } from '@bn-apps/design-system-icons';
import { openInYasgui } from '@utils/yasgui.utils';
import { getIntlLocale } from '@utils/locale.utils';
import type { KeyFigureTable } from '@/types';
import styles from './DataTable.module.scss';

interface CsvRow {
  key: string;
  values: Record<string, string>;
}

interface DataTableProps {
  table: KeyFigureTable;
  id?: string;
}

/**
 * Parse a CSV string (with header row) into an array of row objects.
 * Header names become keys.
 *
 * Handles:
 * - CRLF (`\r\n`) and LF (`\n`) line endings
 * - Quoted fields containing commas, quotes, and newlines
 */
function parseCsv(csv: string): Record<string, string>[] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;

  for (let i = 0; i < csv.length; i += 1) {
    const char = csv[i];

    if (inQuotes) {
      if (char === '"') {
        const nextChar = csv[i + 1];
        if (nextChar === '"') {
          currentField += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        currentField += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        currentRow.push(currentField.trim());
        currentField = '';
      } else if (char === '\r' || char === '\n') {
        currentRow.push(currentField.trim());
        currentField = '';
        if (currentRow.some(f => f !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        if (char === '\r' && csv[i + 1] === '\n') {
          i += 1;
        }
      } else {
        currentField += char;
      }
    }
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim());
  }
  if (currentRow.length > 0 && currentRow.some(f => f !== '')) {
    rows.push(currentRow);
  }

  if (rows.length < 2) return [];

  const headers = rows[0].map(h => h.trim());
  if (headers.length === 0) return [];

  return rows.slice(1).map(cells => {
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      row[h] = (cells[i] ?? '').trim();
    });
    return row;
  });
}

function DataTable({ table, id }: DataTableProps): JSX.Element {
  const { t, i18n } = useTranslation();

  const preferNo = i18n.language !== 'en';
  const getColTitle = (col: { titleEn: string; titleNo: string }): string =>
    preferNo ? col.titleNo : col.titleEn;

  const rows = table.distribution?.csvContent
    ? parseCsv(table.distribution.csvContent)
    : [];

  const updatedStr = table.distribution
    ? table.distribution.created.toLocaleDateString(getIntlLocale(i18n.language))
    : null;

  return (
    <div id={id} className={styles.wrapper}>
      <div className={styles.tableHeading}>
        <Heading level={3} size="panel">
          {table.title}
        </Heading>
        <button
          className={styles.iconButton}
          aria-label={t('datasets.openInYasgui.buttonAria', { title: table.title })}
          title={t('datasets.openInYasgui.button')}
          onClick={() => openInYasgui(table.sparqlQuery, table.title)}
        >
          <ArrowOutward />
        </button>
      </div>

      {!table.distribution ? (
        <Text className={styles.textSubtle}>
          {t('keyFigures.notCalculated')}
        </Text>
      ) : rows.length === 0 ? (
        <Text className={styles.textSubtle}>
          {t('keyFigures.empty.noData')}
        </Text>
      ) : (
        <>
          <Table
            label={table.title}
            columns={table.columns.map((col, colIdx) => ({
              id: col.name,
              header: getColTitle(col),
              numeric: colIdx > 0,
              cell: (row: CsvRow) => row.values[col.name] ?? '',
            }))}
            rows={rows.map((values, index) => ({ key: String(index), values }))}
            rowKey={(row) => row.key}
            minWidth="500px"
          />
          {updatedStr && (
            <Text className={styles.updatedText}>
              {t('keyFigures.table.updated')}: {updatedStr}
            </Text>
          )}
        </>
      )}
    </div>
  );
}

export default DataTable;
